import { ReactNode } from 'react'
import { type MenuOption } from '@opallabs/concepts'
import { SingleSelectContext } from '../contexts'
import { DataProps, NullOnChange, UnwrappedValue } from '../types'

type UncontrolledProps<T> = Omit<DataProps<T>, 'selectedOption'> & {
  children: ReactNode
}

/**
 * @note Use the onChange callback if you want to manage your own
 * selection state or make upstream changes after every change.
 *
 * The initialSelection prop should represent state after any data has been
 * recieved from the server. Downshift manages its own state, and won't update
 * the intial selection after the component has mounted via
 * state management/useEffect. Use `ControlledRoot` to pass selected option if
 * state will be managed by parent compnent.
 *
 * @note If you want to support *deselecting* an option — so that there is no selected value — you should
 * 1. pass `isClearable={true}` to `<SingleSelect.Body>` / `<SingleSelect.SearchableBody>`
 * 2. pass `hideTrailingNode={true}` to `<SingleSelect.Trigger>`
 * 3. pass `hideTrailingNodeUnlessSelected={true}` to `<SingleSelect.MenuItems>`
 * 4. include `trailingNode: <Icon ariaHidden glyph="gem-check-circle" />` on each `Option`
 */
export const Root = <
  Option extends MenuOption<OptionValue>,
  OptionValue extends string | number | boolean = UnwrappedValue<Option>,
>({
  children,
  initialSelection,
  onChange,
  options,
}: UncontrolledProps<Option>) => {
  return (
    <SingleSelectContext.DataProvider
      initialSelection={initialSelection as unknown}
      onChange={onChange as NullOnChange}
      options={options as unknown[]}
    >
      {children}
    </SingleSelectContext.DataProvider>
  )
}

type ControlledProps<T> = DataProps<T> & {
  children: ReactNode
  selectedOption: T | null
}

export const ControlledRoot = <
  Option extends MenuOption<OptionValue>,
  OptionValue extends string | number | boolean = UnwrappedValue<Option>,
>({
  children,
  initialSelection,
  onChange,
  options,
  selectedOption,
}: ControlledProps<Option>) => {
  return (
    <SingleSelectContext.DataProvider
      initialSelection={initialSelection as unknown}
      onChange={onChange as NullOnChange}
      options={options as unknown[]}
      selectedOption={selectedOption as unknown}
    >
      {children}
    </SingleSelectContext.DataProvider>
  )
}
Root.displayName = 'Lapidary: Root ← SingleSelect'
